<template>
  <form class="new-visit-form" ref="formRef" @submit.prevent="">
    <div v-if="createdVisitId == 0">
      <Stepper
        class="new-visit-form__stepper"
        :value="tabIndex"
        @change="handleTabChange"
        :items="tabs"
        :linear="true"
        data-cy="new-visit-form-stepper"
      />
      <!--The following div container displays the page header info; previous button, labels for group visit, visit ID/type/Status and varioius messages-->
      <p
        v-if="showValidationSummary"
        :id="validationSummaryId"
        class="error-message"
      >
        The form cannot be submitted. Please complete each of the fields that
        are marked with a red asterisk.
      </p>
      <div v-if="errorMessages.length > 0" class="error-message-wrapper">
        <p v-for="(message, i) in errorMessages" :key="i" class="error-message">
          {{ message }}
        </p>
      </div>
      <div
        v-if="currentTab.type == TabType.CreateVisit"
        class="new-visit-form__header"
      >
        <div>
          <p>
            Enter information below and attach an inspection report to create
            (ONLY Passover, Review, & Special) visits.
          </p>
        </div>

        <!--The following div container displays the known info for a visit. The first 4 rows are common to all visits. The next two are only displayed for non-scheduled visits. The last row is common to all visits. -->
        <!--This container can also be used for data input for the initial input for scheduled visits only. In such a case, the date label is not visible, the date textbox is visible, and of course the 2 rows for unscheduled visit are not visible -->
        <div class="new-visit-form__unscheduled-visit">
          <div class="new-visit-form__visit-section">
            <div class="new-visit-form__visit-control">
              <label for="visit-type">*Visit Type: </label>
              <ButtonGroup>
                <Button
                  v-for="type in visitTypes"
                  :key="type.value"
                  :togglable="true"
                  type="button"
                  :selected="formData.visitType.value == type.value"
                  :disabled="formSubmitting || loadingPlants"
                  @click="onVisitTypeChange(type)"
                  >{{ type.text }}</Button
                >
              </ButtonGroup>
            </div>
            <div
              v-if="plantFound && !isRegularVisit"
              class="new-visit-form__visit-control"
            >
              <label for="requesting-rc">*Requesting RC: </label>
              <DropDownList
                id="requesting-rc"
                :dataItems="rcPersons"
                v-model="formData.requestingRc"
                :popup-settings="{ className: 'modal-popup' }"
                text-field="FullName"
                data-item-key="PersonJobId"
                name="requesting-rc"
                :disabled="isRegularVisit"
                :required="!isRegularVisit"
              />
            </div>
          </div>

          <div class="new-visit-form__visit-section">
            <h2 class="visit-header">Plant</h2>

            <div class="new-visit-form__visit-control row">
              <Checkbox
                id="plant-checkbox"
                v-model="plantFound"
                data-cy="plant-checkbox"
              />
              <label
                class="form-label"
                for="plant-checkbox"
                data-cy="plant-checkbox-label"
                >Uncheck if plant not found in list</label
              >
            </div>

            <div class="new-visit-form__visit-control">
              <label for="plant-name">*Plant Name: </label>
              <p v-if="formData.visitType.value == null">
                Please select a visit type first.
              </p>
              <DropDownList
                v-if="plantFound"
                id="plant-name"
                name="plant-name"
                :dataItems="plants"
                v-model="formData.plant"
                :popup-settings="{ className: 'modal-popup' }"
                :loading="loadingPlants"
                text-field="PlantName"
                data-item-key="OwnsId"
                @change="onPlantChange"
                :required="formData.visitType.value != null"
                :disabled="formData.visitType.value == null"
              />
              <input
                v-else
                id="plant-name"
                name="plant-name"
                type="text"
                v-model="formData.plantName"
                required
              />
            </div>

            <div
              v-if="!plantFound || formData.plant"
              class="new-visit-form__plant"
            >
              <div class="new-visit-form__visit-control">
                <label for="company-name">*Company Name: </label>
                <input
                  id="company-name"
                  name="company-name"
                  type="text"
                  v-model="formData.companyName"
                  :disabled="plantFound"
                  :required="!plantFound"
                />
              </div>
              <div class="new-visit-form__visit-control">
                <label for="rc-name">*RC Name: </label>
                <DropDownList
                  id="rc-name"
                  name="rc-name"
                  :dataItems="rcPersons"
                  v-model="formData.selectedRc"
                  :popup-settings="{ className: 'modal-popup' }"
                  text-field="FullName"
                  data-item-key="PersonJobId"
                  :disabled="plantFound"
                  :required="!plantFound"
                />
              </div>

              <div class="new-visit-form__visit-control">
                <label for="plant-address">*Plant Address: </label>
                <input
                  id="plant-address"
                  name="plant-address"
                  type="text"
                  v-model="formData.plantAddress"
                  :disabled="plantFound"
                  :required="!plantFound"
                />
              </div>
              <div v-if="plantFound" class="new-visit-form__visit-control">
                <label for="group-leader">*Group Leader: </label>
                <input
                  id="group-leader"
                  name="group-leader"
                  type="text"
                  v-model="formData.groupLeader"
                  disabled
                  required
                />
              </div>

              <div class="new-visit-form__visit-control">
                <label for="plant-city">*Plant City: </label>
                <input
                  id="plant-city"
                  name="plant-city"
                  type="text"
                  v-model="formData.plantCity"
                  :disabled="plantFound"
                  :required="!plantFound"
                />
              </div>
              <div class="new-visit-form__visit-control">
                <label for="plant-state">*Plant State: </label>
                <DropDownList
                  id="plant-state"
                  name="plant-state"
                  :dataItems="dropdownStates"
                  v-model="formData.plantState"
                  :popup-settings="{ className: 'modal-popup' }"
                  :disabled="plantFound"
                />
              </div>
              <div class="new-visit-form__visit-control">
                <label for="plant-country">*Plant Country: </label>
                <DropDownList
                  id="plant-country"
                  name="plant-country"
                  :dataItems="countries"
                  v-model="formData.plantCountry"
                  :popup-settings="{ className: 'modal-popup' }"
                  :disabled="plantFound"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="new-visit-form__actions">
          <Button
            class="new-visit-form__submit"
            type="submit"
            @click="changeTab(TabType.Confirmation)"
            themeColor="primary"
            :disabled="formSubmitting"
            >Continue</Button
          >
        </div>
      </div>

      <div v-if="currentTab.type == TabType.Confirmation">
        <div class="new-visit-form__confirmation">
          <div class="new-visit-form__visit-section">
            <div class="new-visit-form__visit-control">
              <p>Visit Type:</p>
              <p class="bold">{{ formData.visitType.text }}</p>
            </div>
            <div
              v-if="plantFound && !isRegularVisit"
              class="new-visit-form__visit-control"
            >
              <p>Requesting RC:</p>
              <p class="bold">{{ formData.requestingRc?.FullName }}</p>
            </div>
            <div class="new-visit-form__visit-control">
              <p>Plant Name:</p>
              <p class="bold">
                {{ formData.plant?.PlantName ?? formData.plantName }}
              </p>
            </div>
            <div v-if="matchingVisit" class="new-visit-form__visit-control">
              <p>Matching Visit:</p>
              <a
                v-if="matchingVisit.RfrCreatedVisit"
                :class="{ saved: matchingVisit.HasSavedData }"
                class="visit-link"
                @click="redirectToRfrCreatedVisit(matchingVisit!.Id, true)"
                data-cy="visit-link"
              >
                Not Available
              </a>
              <a
                v-else
                :href="`RfrVisitInspectionForm?category=Inspection&mode=new&visitID=${matchingVisit.Id}&menu=VisitsAsRfr`"
                :class="{ saved: matchingVisit.HasSavedData }"
                target="_blank"
                class="visit-link"
                data-cy="visit-link"
              >
                {{ matchingVisit.Id }}
              </a>
            </div>
          </div>
          <div
            v-if="loadingMatchingVisit"
            class="new-visit-form__visit-control"
          >
            <Loader class="visit-loader" size="large" type="pulsing" />
          </div>
          <div
            v-else-if="matchingVisit && showMatchingVisitWarning"
            class="new-visit-form__visit-control"
          >
            <p class="warning-message">
              A matching visit was found. Do you want to continue creating a new
              visit?
            </p>
            <div class="new-visit-form__actions">
              <Button
                type="button"
                @click="showMatchingVisitWarning = false"
                themeColor="secondary"
                >Yes</Button
              >
              <Button
                type="button"
                @click="changeTab(TabType.CreateVisit)"
                themeColor="primary"
                >No</Button
              >
            </div>
          </div>
          <div v-else class="new-visit-form__actions">
            <Button
              class="new-visit-form__submit"
              type="submit"
              @click="onSubmit($event)"
              themeColor="primary"
              :disabled="formSubmitting"
              >Create Visit And Start Inspection</Button
            >
          </div>
        </div>
      </div>
    </div>
    <!-- After visit is created show visit id and action to Inspection page -->
    <div v-else class="new-visit-form__results">
      <Button
        type="button"
        themeColor="primary"
        @click="onStartInspectionClicked"
        >Start Inspection</Button
      >
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { Checkbox } from "@progress/kendo-vue-inputs";
import { EnumVisitType } from "@/Domain/EnumVisitType";
import { ButtonGroup, Button } from "@progress/kendo-vue-buttons";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { InspectionRfrPlant } from "@/Domain/InspectionRfrPlant";
import type { EirRcPerson } from "@/Domain/EirRcPerson";
import type { CreateVisit } from "@/Models/Requests/CreateVisit";
import type { CreateVisitNoPlantId } from "@/Models/Requests/CreateVisitNoPlantId";
import { NewVisitsService } from "@/shared/services/NewVisitsService";
import { Stepper } from "@progress/kendo-vue-layout";
import { CacheService } from "@/shared/services/CacheService";
import type { VisitDisplay } from "@/Domain/VisitDisplay";
import { Loader } from "@progress/kendo-vue-indicators";
import { checkIcon, trackChangesEnableIcon } from "@progress/kendo-svg-icons";

const main = useMainStore();
const notificationStore = useNotificationStore();

enum TabType {
  CreateVisit = 0,
  Confirmation = 1,
}

const emit = defineEmits<{
  (e: "submittingChanged", value: boolean): void;
}>();

interface FormData {
  visitType: { text: string; value: EnumVisitType | null };
  requestingRc: EirRcPerson | null;
  selectedRc: EirRcPerson | null;
  plantName: string;
  companyName: string;
  rcName: string;
  plant: InspectionRfrPlant | null;
  plantAddress: string;
  groupLeader: string;
  plantCity: string;
  plantState: string;
  plantCountry: string;
}

const showValidationSummary = ref<boolean>(false);
const validationSummaryId = "validation-summary";
const errorMessages = ref<string[]>([]);

const plantFound = ref<boolean>(true);
const formSubmitting = ref<boolean>(false);

const formRef = ref<HTMLFormElement | null>(null);

const visitTypes = [
  { text: "Regular", value: EnumVisitType.Regular },
  { text: "Special Production", value: EnumVisitType.SpecialProduction },
  { text: "Passover", value: EnumVisitType.Passover },
  { text: "Review", value: EnumVisitType.Review },
];

const plants = ref<InspectionRfrPlant[]>([]);

const states = ref<{ Item1: string; Item2: string }[]>([]);
const countries = ref<string[]>([]);
const rcPersons = ref<EirRcPerson[]>([]);
const loadingPlants = ref<boolean>(false);

const createdVisitId = ref<number>(0);

const matchingVisit = ref<VisitDisplay | null>(null);
const loadingMatchingVisit = ref<boolean>(false);
const showMatchingVisitWarning = ref<boolean>(false);

const tabs = computed(() => [
  {
    type: TabType.CreateVisit,
    label: "Visit Information",
    svgIcon: trackChangesEnableIcon,
  },
  {
    type: TabType.Confirmation,
    label: "Confirmation",
    svgIcon: checkIcon,
  },
]);

const tabIndex = ref(0);

const currentTab = computed(() => {
  return tabs.value[tabIndex.value];
});

const handleTabChange = function (e: any) {
  const tab = tabs.value[e.value];
  if (
    tab.type == TabType.Confirmation &&
    (!formRef.value?.checkValidity() || !validateForm())
  )
    return;

  if (tab.type == TabType.Confirmation) {
    getMatchingVisitAsync();
  }

  tabIndex.value = e.value;
};

const changeTab = function (tabType: TabType) {
  if (
    tabType == TabType.Confirmation &&
    (!formRef.value?.checkValidity() || !validateForm())
  )
    return;

  if (tabType == TabType.Confirmation) {
    getMatchingVisitAsync();
  }

  const index = tabs.value.findIndex((t) => t.type === tabType);
  if (index === -1) return;
  tabIndex.value = index;
};

const getDefaultFormData = function (): FormData {
  return {
    visitType: { text: "", value: null },
    requestingRc: null,
    selectedRc: null,
    plantName: "",
    companyName: "",
    rcName: "",
    plant: null,
    plantAddress: "",
    groupLeader: "",
    plantCity: "",
    plantState: "",
    plantCountry: "",
  };
};

const formData = ref<FormData>(getDefaultFormData());

const defaultStateValue = "Select State...";

const dropdownStates = computed(() => {
  return [
    defaultStateValue,
    ...states.value.map((state) => {
      return state.Item2;
    }),
  ];
});

const isRegularVisit = computed(() => {
  return formData.value.visitType.value == EnumVisitType.Regular;
});

const validateForm = function () {
  errorMessages.value = [];
  showValidationSummary.value = false;

  // Validate that plant state is selected if plant country is US
  if (
    !plantFound.value &&
    formData.value.plantCountry == "USA" &&
    (formData.value.plantState == "" ||
      formData.value.plantState == defaultStateValue)
  ) {
    errorMessages.value.push("Please select a state for the plant.");
  }

  // Validate plant country and state
  if (
    !plantFound.value &&
    !formData.value.plantCountry &&
    (!formData.value.plantState ||
      formData.value.plantState == defaultStateValue)
  ) {
    errorMessages.value.push("Please select a country or state for the plant.");
  }

  // Validate visit type
  if (!formData.value.visitType.value && formData.value.visitType.value != 0) {
    errorMessages.value.push("Please select a visit type.");
  }

  // Show validation summary if there are errors
  if (errorMessages.value.length > 0) {
    showValidationSummary.value = true;

    setTimeout(() => {
      const formElement = document.getElementById(validationSummaryId);
      formElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);

    return false;
  }

  return true;
};

const getVisitUrl = function () {
  let subType = "short";
  if (formData.value.visitType?.value == EnumVisitType.Regular)
    subType = "long";
  return `RfrVisitInspectionForm?category=Inspection&mode=new&subtype=${subType}&menu=VisitsAsRfr`;
};

const getMatchingVisitAsync = async function () {
  if (!formData.value.plant) return;

  loadingMatchingVisit.value = true;
  matchingVisit.value = null;
  showMatchingVisitWarning.value = false;

  const matchingVisitPromise = api.get("GetMatchingVisit", {
    plantId: formData.value.plant!.PlantId,
    type: formData.value.visitType.value!,
  });

  await handleResponsePromiseAsync(
    matchingVisitPromise,
    (data) => {
      matchingVisit.value = data;
      if (data) showMatchingVisitWarning.value = true;
    },
    "NewVisitForm.vue",
    "getMatchingVisitAsync"
  );

  loadingMatchingVisit.value = false;
};

const onStartInspectionClicked = function () {
  CacheService.set("RfrVisitInspectionFormId", createdVisitId.value);
  window.location.href = getVisitUrl();
};

const setFormSubmitting = function (value: boolean) {
  formSubmitting.value = value;
  emit("submittingChanged", value);
};

const redirectToRfrCreatedVisit = function (
  visitId: number,
  newTab: boolean = false
) {
  CacheService.set("RfrVisitInspectionFormId", visitId);
  if (newTab) window.open(getVisitUrl(), "_blank");
  else window.location.href = getVisitUrl();
};

const onSubmit = async function (redirectToInspection: boolean = true) {
  if (!formRef.value?.checkValidity() || !validateForm()) return;

  setFormSubmitting(true);

  let visitId = 0;

  if (plantFound.value) visitId = await createVisitAsync();
  else visitId = await createVisitNoPlantIdAsync();

  if (!visitId) {
    notificationStore.notify({
      message: "Error submitting form. Please try again later.",
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
    setFormSubmitting(false);
    return;
  }

  const plantName = plantFound.value
    ? formData.value.plant!.PlantName
    : formData.value.plantName;
  NewVisitsService.addNewVisit(
    visitId,
    formData.value.visitType.value!,
    plantName
  );

  if (redirectToInspection) {
    redirectToRfrCreatedVisit(visitId);
  } else {
    createdVisitId.value = visitId;
  }

  setFormSubmitting(false);
  console.log("NewVisitForm, submitting", formData.value);
};

const onVisitTypeChange = function (visitType: {
  text: string;
  value: EnumVisitType | null;
}) {
  if (formData.value.visitType.value == visitType.value) return;

  formData.value.visitType = visitType;
  formData.value.plant = null;
  plants.value = [];
  onPlantChange();
  if (formData.value.visitType.value == null) return;
  getPlantsAsync();
};

const onPlantChange = async function () {
  const plant = formData.value.plant;
  formData.value.companyName = plant?.CompanyName ?? "";
  formData.value.plantAddress = plant?.Address ?? "";
  formData.value.plantCity = plant?.City ?? "";
  formData.value.plantState =
    states.value.find((s) => s.Item1 == plant?.State)?.Item2 ?? "";
  formData.value.plantCountry =
    countries.value.find((c) => c == plant?.Country) ?? "";
  formData.value.groupLeader = "";
  formData.value.selectedRc = null;

  if (!plant) return;
  getCompanyRcInfoAsync(plant.CompanyId);
};

const getPlantsAsync = async function () {
  const plantsPromise = api.get("RfrPlantList", {
    visitType: formData.value.visitType.text,
  });

  loadingPlants.value = true;

  await handleResponsePromiseAsync(
    plantsPromise,
    (data) => {
      plants.value = data;
    },
    "NewVisitForm.vue",
    "getPlants"
  );

  loadingPlants.value = false;
};

const getStatesAsync = async function () {
  const statesPromise = api.get("GetStates", {});

  await handleResponsePromiseAsync(
    statesPromise,
    (data) => {
      states.value = data;
    },
    "NewVisitForm.vue",
    "getStatesAsync"
  );
};

const getCountriesAsync = async function () {
  const countriesPromise = api.get("CountryList", {});

  await handleResponsePromiseAsync(
    countriesPromise,
    (data) => {
      countries.value = data;
    },
    "NewVisitForm.vue",
    "getCountriesAsync"
  );
};

const getRcPersonsAsync = async function () {
  const rcPersonsPromise = api.get("GetEirRcList", {});

  await handleResponsePromiseAsync(
    rcPersonsPromise,
    (data) => {
      rcPersons.value = data;
    },
    "NewVisitForm.vue",
    "getRcPersonsAsync"
  );
};

const getCompanyRcInfoAsync = async function (companyId: number) {
  const companyRcInfoPromise = api.get("GetCompanyRcInfo", {
    userLogin: main.userLogin,
    compid: companyId.toString(),
  });

  await handleResponsePromiseAsync(
    companyRcInfoPromise,
    (data) => {
      formData.value.groupLeader = `${data.GroupLeaderName} (${data.GroupLeaderEmail})`;
      formData.value.selectedRc =
        rcPersons.value.find((rc) => rc.PersonJobId == data.RcPersonJobId) ??
        null;
    },
    "NewVisitForm.vue",
    "getCompanyRcInfoAsync"
  );
};

const createVisitAsync = async function () {
  const createVisitPromise = api.post("CreateVisit", {
    OwnsId: formData.value.plant!.OwnsId,
    VisitType: formData.value.visitType.value,
    SenderRcName: !isRegularVisit.value
      ? formData.value.requestingRc?.FullName ?? ""
      : "",
    SenderRcEmail: !isRegularVisit.value
      ? formData.value.requestingRc?.Email ?? ""
      : "",
  } as CreateVisit);

  let result = 0;

  await handleResponsePromiseAsync(
    createVisitPromise,
    (data) => {
      result = data;
    },
    "NewVisitForm.vue",
    "createVisitAsync"
  );

  return result;
};

const createVisitNoPlantIdAsync = async function () {
  const createVisitNoPlantIdPromise = api.post("CreateVisitNoPlantId", {
    CompanyName: formData.value.companyName,
    PlantName: formData.value.plantName,
    PlantAddress: formData.value.plantAddress,
    PlantCity: formData.value.plantCity,
    PlantState: formData.value.plantState,
    PlantCountry: formData.value.plantCountry,
    VisitType: formData.value.visitType.value,
    RcName: formData.value.selectedRc!.FullName,
  } as CreateVisitNoPlantId);

  let result = 0;

  await handleResponsePromiseAsync(
    createVisitNoPlantIdPromise,
    (data) => {
      result = data;
    },
    "NewVisitForm.vue",
    "createVisitNoPlantIdAsync"
  );

  return result;
};

onBeforeMount(async () => {
  getRcPersonsAsync();
  getStatesAsync();
  getCountriesAsync();
});
</script>

<style lang="scss">
.new-visit-form {
  &__question-checkbox {
    .k-checkbox {
      width: 21px;
      height: 21px;
    }
  }
}
</style>

<style scoped lang="scss">
.new-visit-form {
  min-width: 260px;

  &__stepper {
    margin-bottom: 20px;
  }

  .error-message-wrapper {
    margin: 20px 0;
  }

  .error-message {
    font-weight: bold;
    color: red;
  }

  .inspection-comments-view {
    margin-bottom: 20px;
  }

  &__header {
    margin-bottom: 20px;
  }

  &__unscheduled-visit {
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin-top: 16px;
  }

  &__date-range {
    margin-bottom: 20px;
  }

  &__visit-cell {
    flex: 1;
  }

  &__visit-control {
    display: flex;
    margin-bottom: 20px;
    flex-direction: column;
    gap: 8px;

    .warning-message {
      text-align: center;
    }

    & .bold {
      font-weight: bold;
    }

    &.row {
      flex-direction: row;
    }

    &.grow {
      flex: 1;
    }

    & > label {
      text-wrap: nowrap;
    }

    & > .k-dropdownlist {
      min-width: 120px;
    }
  }

  &__plant-guide-new {
    background: #efefef;
    padding: 12px;
    border-radius: 8px;
  }

  .visit-header {
    background: #efefef;
    padding: 15px;
    font-size: 16px;
    margin-bottom: 20px;
    font-weight: bold;
    border-radius: 8px;
  }

  &__question-checkbox {
    margin-bottom: 12px;
    display: block;
  }

  &__non-special,
  &__special {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  &__email-recipients {
    width: 100%;
    margin-bottom: 12px;
  }

  &__actions {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: center;

    & > button {
      min-width: 60px;
    }
  }

  &__submit {
    max-width: 200px;
  }

  &__text {
    font-weight: bold;
  }

  &__results {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;

    .created-visit-label {
      font-size: 24px;
      font-weight: bold;
    }
  }

  .visit-loader {
    margin: 0 auto;
  }

  .visit-link {
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;

    &.saved {
      color: #28a745;
    }
  }
}
</style>
